import { DashboardProposedPanelsAPI } from '@/API/DashboardProposedPanels'
import type { AppDispatch } from '@/redux'
import { dashboardCreationActions } from '@/redux/slice/dashboardCreationSlice'

/**
 * DashboardProposedPanelsService — deterministic panel-proposal thunk
 * (Slice 1.2, `plans/2026-04-29-step-5-deterministic-panels-and-chat-context.md`
 * §Slice 1.2 Frontend).
 *
 * Behaviour:
 *   1. dispatch setProposedPanelsStatus('loading')
 *   2. POST /v1/dashboards/:id/proposed-panels
 *   3. on 200 → setProposedPanels(proposedPanels) + setProposedPanelsStatus('ready')
 *      (setProposedPanels also auto-checks default-on panels via the existing
 *      reducer logic — no separate confirmedPanelIds dispatch needed)
 *   4. on 409 FSM conflict → setProposedPanelsStatus('fsm_conflict')
 *   5. on any other error → setProposedPanelsStatus('error')
 *
 * Idempotency: the caller (FiltersBlock.handleConfirm) passes a freshly
 * generated UUID key per click invocation. The backend uses this to guard
 * against rapid double-confirm producing duplicate `flow_context` writes.
 */
export const proposePanelsThunk = (
  token: string,
  dashboardId: string,
  idempotencyKey?: string
) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(dashboardCreationActions.setProposedPanelsStatus('loading'))

    const result = await DashboardProposedPanelsAPI.proposePanels(token, {
      dashboardId,
      idempotencyKey,
    })

    if (result.err !== undefined || !result.data) {
      // 409 signals the FSM gate is too early — user confirmed out of order.
      // Show a back-to-Step-4 affordance in PanelsProposedBlock.
      const isFsmConflict = result.status === 409

      dispatch(
        dashboardCreationActions.setProposedPanelsStatus(
          isFsmConflict ? 'fsm_conflict' : 'error'
        )
      )
      return
    }

    // setProposedPanels auto-confirms default-on panels via the existing
    // reducer logic (filters p.defaultInFirstDashboard → confirmedPanelIds).
    dispatch(dashboardCreationActions.setProposedPanels(result.data.proposedPanels))
    dispatch(dashboardCreationActions.setProposedPanelsStatus('ready'))
  }
